import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const layers = [];
const focusableSelector = 'button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]';
let restorePage;

function lockPage() {
  const body = document.body;
  const root = document.getElementById('root');
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  const previousStyles = Object.fromEntries(['position', 'top', 'left', 'right', 'overflow'].map((key) => [key, body.style[key]]));
  const rootWasInert = root?.hasAttribute('inert');

  Object.assign(body.style, { position: 'fixed', top: `-${scrollY}px`, left: '0', right: '0', overflow: 'hidden' });
  root?.setAttribute('inert', '');

  return () => {
    Object.assign(body.style, previousStyles);
    if (!rootWasInert) root?.removeAttribute('inert');
    window.scrollTo(scrollX, scrollY);
  };
}

function updateLayers() {
  layers.forEach((layer, index) => {
    layer.backdrop.inert = index !== layers.length - 1;
    layer.backdrop.style.zIndex = String(100 + index);
  });
}

// Portals keep dialogs outside page stacking contexts and scrolling containers.
export function Modal({ children, onClose, labelledBy, describedBy, role = 'dialog', className = 'modal', backdropClassName = '', busy = false }) {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  onCloseRef.current = onClose;
  busyRef.current = busy;

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    const previouslyFocused = document.activeElement;
    if (layers.length === 0) restorePage = lockPage();
    const layer = { dialog, backdrop: dialog.parentElement };
    layers.push(layer);
    updateLayers();

    const focusableElements = () => Array.from(dialog.querySelectorAll(focusableSelector))
      .filter((element) => !element.closest('[hidden], [inert]'));
    const initialFocus = dialog.querySelector('[data-autofocus]') ?? focusableElements()[0] ?? dialog;
    initialFocus.focus({ preventScroll: true });

    function handleKeyDown(event) {
      if (layers.at(-1) !== layer) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (!busyRef.current) onCloseRef.current?.();
      }
      if (event.key !== 'Tab') return;
      const elements = focusableElements();
      const first = elements[0] ?? dialog;
      const last = elements.at(-1) ?? dialog;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      layers.splice(layers.indexOf(layer), 1);
      updateLayers();
      if (layers.length === 0) {
        restorePage?.();
        restorePage = undefined;
      }
      if (previouslyFocused?.isConnected && !previouslyFocused.closest('[inert]')) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, []);

  return createPortal(
    <div className={`modal-backdrop ${backdropClassName}`.trim()} onClick={(event) => {
      event.stopPropagation();
      if (event.target === event.currentTarget && !busyRef.current) onCloseRef.current?.();
    }}>
      <div ref={dialogRef} className={className} role={role} aria-modal="true" aria-labelledby={labelledBy} aria-describedby={describedBy} aria-busy={busy || undefined} tabIndex={-1}>
        {children}
      </div>
    </div>,
    document.body,
  );
}
